import getCacheZeroQuery from './getCacheZeroQuery';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { now } from 'owa-datetime';
import { getGuid } from 'owa-guid';
import { getServiceTimeout } from 'owa-readwrite-recipient-well/lib/actions/people/getServiceTimeout';
import postProcessSuggestionsResult from 'owa-readwrite-recipient-well/lib/actions/people/postProcessSuggestionsResult';
import processResponseResults from 'owa-readwrite-recipient-well/lib/actions/people/processResponseResults';
import setIsSearching from 'owa-readwrite-recipient-well/lib/actions/setIsSearching';
import updateCurrentRenderedQueryString from 'owa-readwrite-recipient-well/lib/actions/updateCurrentRenderedQueryString';
import ReadWriteRecipientWellStore from 'owa-readwrite-recipient-well/lib/store/schema/ReadWriteRecipientWellStore';
import recipientWellStore from 'owa-readwrite-recipient-well/lib/store/store';
import tryGetRecipientsFromViewState from 'owa-readwrite-recipient-well/lib/utils/tryGetRecipientsFromViewState';
import addTransactionComplete from 'owa-recipient-common/lib/utils/addTransactionComplete';
import addTransactionFailed from 'owa-recipient-common/lib/utils/addTransactionFailed';
import getTransactionIdFromResponse from 'owa-recipient-common/lib/utils/getTransactionIdFromResponse';
import { lazySearchSuggestions } from 'owa-recipient-suggestions';
import shouldUse3SPeopleSuggestions from 'owa-recipient-suggestions/lib/util/shouldUse3SPeopleSuggestions';
import { FindResultType } from 'owa-recipient-types/lib/types/FindControlViewState';
import ReadWriteRecipientViewState from 'owa-recipient-types/lib/types/ReadWriteRecipientViewState';
import RecipientWellWithFindControlViewState from 'owa-recipient-types/lib/types/RecipientWellWithFindControlViewState';
import { action, select } from 'satcheljs/lib/legacy';
import {
    addPendingRequest,
    removePendingRequest,
} from 'owa-readwrite-recipient-well/lib/actions/updatePendingRequestActions';
import {
    FIND_PEOPLE_REQUEST_COUNT_THRESHOLD,
    MAX_ENTRIES_TYPEDOWN,
} from 'owa-readwrite-recipient-well/lib/utils/constants';
import FindControlViewState, {
    DirectorySearchType,
} from 'owa-recipient-types/lib/types/FindControlViewState';
import {
    resetFeedbackState,
    populateFeedbackStateNetwork,
    updateFeedbackStateNetwork,
} from 'owa-readwrite-recipient-well/lib/utils/populateFeedbackState';

export interface ReadWriteRecipientWellStoreState {
    store: ReadWriteRecipientWellStore;
}

interface TimeOutCheck {
    isTimedOut: boolean;
    isCacheEmpty: boolean;
}

let handleLongDelay = (
    recipientWell: FindControlViewState,
    recipients: ReadWriteRecipientViewState[],
    didTimeOut: TimeOutCheck
) => async () => {
    didTimeOut.isTimedOut = true;
    getCacheZeroQuery(recipientWell, recipients, true /*isFallback*/);
};

export default action('getLiveZeroQuery')(
    select<ReadWriteRecipientWellStoreState>({
        store: () => recipientWellStore,
    })(async function getLiveZeroQuery(
        recipientWell: FindControlViewState | RecipientWellWithFindControlViewState,
        recipientsToExclude?: ReadWriteRecipientViewState[],
        state?: ReadWriteRecipientWellStoreState
    ): Promise<void> {
        resetFeedbackState(recipientWell);

        let recipients = tryGetRecipientsFromViewState(recipientWell, recipientsToExclude);

        // If the pending FP request threshold has not been hit
        // Continue with the live zero query call
        // Otherwise fallback to getting results from the cache
        if (state && state.store.pendingFindPeopleRequests < FIND_PEOPLE_REQUEST_COUNT_THRESHOLD) {
            let datapoint: PerformanceDatapoint = new PerformanceDatapoint('SearchZeroQuery');
            setIsSearching(recipientWell, true);
            let didTimeOut: TimeOutCheck = { isTimedOut: false, isCacheEmpty: false };
            let timeout = setTimeout(
                handleLongDelay(recipientWell, recipients, didTimeOut),
                getServiceTimeout()
            );
            let transactionStartTime = now();
            let transactionId = '';
            const correlationId = getGuid();
            try {
                // Add each recipient email addresses to the response
                let currentRecipients = recipients.map(item => {
                    return item.persona.EmailAddress.EmailAddress;
                });

                populateFeedbackStateNetwork(
                    recipientWell,
                    transactionStartTime,
                    correlationId,
                    false /*searchDirectory*/
                );

                addPendingRequest(correlationId);
                let response = await lazySearchSuggestions
                    .import()
                    .then(suggestionService =>
                        suggestionService(
                            recipientWell.userIdentity,
                            '',
                            false /*searchDirectory*/,
                            correlationId,
                            recipientWell.findPeopleFeedbackManager,
                            currentRecipients
                        )
                    );
                removePendingRequest(correlationId);
                clearTimeout(timeout);
                const isSameQueryString = recipientWell.queryString == '';

                if (!didTimeOut.isTimedOut && isSameQueryString) {
                    updateFeedbackStateNetwork(recipientWell, false /*searchDirectory*/, response);
                    updateCurrentRenderedQueryString(recipientWell, '');
                    recipientWell.directorySearchType = DirectorySearchType.None;
                    processResponseResults(
                        recipientWell.findResultSet,
                        response,
                        false,
                        MAX_ENTRIES_TYPEDOWN
                    );

                    transactionId = getTransactionIdFromResponse(response);
                    addTransactionComplete(
                        recipientWell.findResultSet,
                        recipientWell,
                        transactionStartTime,
                        transactionId
                    );
                    // If we got results, proceed as normal, otherwise check the cache
                    if (recipientWell.findResultSet.length > 0) {
                        setIsSearching(recipientWell, false);
                        let resultType = FindResultType.FindPeople;
                        postProcessSuggestionsResult(
                            recipientWell,
                            resultType,
                            0 /*numberOfCacheResults*/,
                            false /*resolveIfSingle*/
                        );
                    } else {
                        getCacheZeroQuery(recipientWell, recipients, true /*fallback*/);
                    }
                }

                datapoint.addCustomData([
                    didTimeOut.isTimedOut,
                    didTimeOut.isCacheEmpty,
                    isSameQueryString,
                    shouldUse3SPeopleSuggestions(),
                ]);
                datapoint.end();
            } catch (error) {
                removePendingRequest(correlationId);
                datapoint.endWithError(DatapointStatus.ServerError, error);
                addTransactionFailed(recipientWell, transactionStartTime, transactionId);
                getCacheZeroQuery(recipientWell, recipients, true /*fallback*/);
            }
        } else {
            getCacheZeroQuery(recipientWell, recipients, true /*fallback*/);
        }
        setIsSearching(recipientWell, false);
    })
);
